import type { SkyProduct } from '../../hooks/socialdash/useSkyProducts'

/**
 * The slug the Social Media menu entry lands on, and the tab selected when the
 * URL names no product. Kept here rather than in the route table because the
 * tabs and the redirect must agree about it — two copies is how "Social Media"
 * opens on one department and highlights another.
 */
export const DEFAULT_SKY_PRODUCT = 'brand'

/** The admin view sits at the END of the tab strip, whatever order the manifest lists it in. */
export const ADMIN_SLUG = 'all'

/**
 * Which department the "Token spend" button should land on from a dashboard.
 *
 * ALWAYS the all-products view when the viewer has it (Kai, 2026-08-18): spend
 * is a budget question, and the first thing anyone asks of a budget is the
 * total. Landing on the department you happened to be reading answered a
 * narrower question than the one the button invites, and the page's own
 * dropdown filters back down in one click — so the total is the better default
 * in the direction that is cheap to undo.
 *
 * 🔴 IT FALLS BACK TO THE CURRENT DEPARTMENT RATHER THAN ASSUMING `all` EXISTS.
 * The catalogue is audience-filtered server-side, so a viewer restricted to one
 * department never receives the `all` card — and sending them to a slug the
 * server refuses would turn a working button into an error page. This is not a
 * permission check (the server owns that, and the URL is typeable regardless);
 * it is choosing a landing slug from what the viewer demonstrably has.
 *
 * Sky's per-department access story is not settled yet. When it is, this is the
 * one place the landing rule lives.
 */
export function spendLandingProduct(products: SkyProduct[], current: string): string {
  return products.some((p) => p.slug === ADMIN_SLUG) ? ADMIN_SLUG : current
}

/**
 * Departments in tab order: the catalogue's own order, with `all` forced last.
 *
 * The catalogue is already audience-filtered server-side (ListSkyProducts drops
 * anything canViewProduct refuses), so a non-admin simply never receives the
 * `all` card and never sees the tab. That is deliberately NOT re-checked here —
 * a frontend role check would be a second copy of a rule the server owns, and
 * the URL is typeable regardless.
 */
export function orderProductTabs(products: SkyProduct[]): SkyProduct[] {
  const departments = products.filter((p) => p.slug !== ADMIN_SLUG)
  const admin = products.filter((p) => p.slug === ADMIN_SLUG)
  return [...departments, ...admin]
}

/**
 * The Admin tab's value. Not a product slug: it opens the Post data page, not a
 * dashboard, so it can never collide with a manifest product.
 */
export const ADMIN_TAB = 'admin'

/**
 * The strip with the Admin tab appended, for a viewer the server has shown to
 * be an admin.
 *
 * Post data is admins only (Kai, 2026-09-10). It used to be a button on every
 * dashboard, which handed a whole-job editor — every campaign, the unattributed
 * posts, the non-Sprinklr archive — to every department. It is a tab of its own
 * now, after All products, the other admin view.
 *
 * 🔴 THE EVIDENCE IS THE `all` CARD, NOT A ROLE CHECK. The catalogue is
 * audience-filtered server-side and only admins receive `all`, so its presence
 * already answers the question — exactly as `spendLandingProduct` uses it. A
 * frontend role check would be a second copy of a rule the server owns. This
 * only decides whether the tab is OFFERED; the server refuses the page's writes
 * and exports to a non-admin regardless, and the URL is typeable either way.
 */
export function withAdminTab(tabs: SkyProduct[]): SkyProduct[] {
  if (!tabs.some((t) => t.slug === ADMIN_SLUG)) return tabs
  return [...tabs, { slug: ADMIN_TAB, name: 'Admin', description: 'Post data' }]
}

/**
 * The Reconciliation tab's value. Not a product slug: it opens the
 * reconciliation page, which every Sky viewer may open (Kai, 16 Oct 2026:
 * "anybody can see why the numbers are the way they are").
 */
export const RECONCILIATION_TAB = 'reconciliation'

/**
 * The strip with the Reconciliation tab appended, for a viewer with no Admin
 * tab.
 *
 * An admin opens it under Admin, beside Post data and Influencer data (Kai,
 * 3 Nov 2026: "it should be a tab underneath admin"), so their top row does
 * not carry it. Every other Sky viewer may still open it (Kai, 16 Oct 2026),
 * and has no Admin tab to find it under, so for them it stays on the top row.
 */
export function withReconciliationTab(tabs: SkyProduct[]): SkyProduct[] {
  if (tabs.length === 0) return tabs
  if (tabs.some((t) => t.slug === ADMIN_TAB)) return tabs
  return [...tabs, { slug: RECONCILIATION_TAB, name: 'Reconciliation', description: "How our numbers relate to Sky's Sprinklr table" }]
}

/**
 * Which top-row tab is selected for a page's slug. The Reconciliation page
 * selects Admin when Admin holds it; an unknown slug selects nothing.
 */
export function selectedTab(tabs: SkyProduct[], active: string): string | false {
  if (tabs.some((t) => t.slug === active)) return active
  if (active === RECONCILIATION_TAB && tabs.some((t) => t.slug === ADMIN_TAB)) return ADMIN_TAB
  return false
}

/**
 * The params a department tab's navigate call needs: the target slug. (It
 * also carried a `?source=` unlock for the Apify archive until 21 Oct 2026,
 * when the ingest-source control was retired.)
 */
export function productTabParams(slug: string): Record<string, string> {
  return { product: slug }
}
